import * as React from "react";
// UI frameworks
import { Form, Icon, Row, Col, Radio } from "antd";
import { FormComponentProps } from "antd/lib/form";
// Shared components
import Button from "components/Button";
import Box from "components/Box";
import InputNumber from "components/InputNumber";
// Utilities and hooks
import { useSell } from "../..";
import addCommas from "helpers/index";
import { persianNumber } from "helpers/persian";
import { inject, observer } from "mobx-react";
import UserStore from "state/user";
// Styles
import styles from "./index.module.scss";
import Maybe from "components/Maybe";
import { debounce, omit } from "lodash";

interface IProps extends FormComponentProps {
	user: UserStore;
}

const FirstStep: React.FC<IProps> = (props: IProps) => {
	const { state, dispatch } = useSell();
	const [selectedRadio, setSelectedRadio] = React.useState("unit");
	const [insufficient, setSufficient] = React.useState(true);
	const { getFieldDecorator, setFieldsValue } = props.form;
	const price = Number(props.user.goldSellPrice) || 0;

	React.useEffect(() => {
		setFieldsValue({
			...omit(state, ["current"]),
		});
	}, [setFieldsValue, state]);

	function useDebouncedCallback(callback: (value: any) => void, delay: number) {
		const d = callback;

		const callbackfunc = React.useCallback(debounce(d, delay), []);

		return [callbackfunc];
	}

	const [debouncedFunction] = useDebouncedCallback((value: any) => {
		const unit = Number(value) / props.user.goldSellPrice;
		// round the amount
		setFieldsValue({
			unit: unit.toFixed(4),
		});
	}, 1000);

	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();
		props.form.validateFieldsAndScroll((err, { unit, amount }) => {
			if (!err) {
				dispatch({ unit, amount, current: 1 });
			}
		});
	}

	function handleUnitValidator(_: any, value: string, callback: any) {
		if (selectedRadio === "unit") {
			if (!value) {
				callback("لطفا مقدار این فیلد را وارد کنید");
			} else if (value && !Number(value)) {
				callback("لطفا مقدار عددی وارد کنید");
			} else if (Number(value) > props.user.goldCredit) {
				setSufficient(true)
				callback("موجودی طلای شما کافی نمیباشد")
			} else {
				setSufficient(false)
				callback();
			}

			return;
		}

		callback();
	}

	function handleAmountValidator(_: any, value: string, callback: any) {
		if (selectedRadio === "amount") {
			if (!value) {
				callback("لطفا مقدار این فیلد را وارد کنید");
			} else if (value && !Number(value)) {
				callback("لطفا مقدار عددی وارد کنید");
			} else {
				callback();
			}

			return;
		}

		callback();
	}

	return (
		<div className={styles["first-step"]}>
			<Row className={styles["first-step__boxes"]} type="flex" justify="center">
				<Col md={10} xs={24}>
					<Box title="قیمت فروش طلا در سامانه" prefix="ریال">
						{addCommas(props.user.goldSellPrice)}
					</Box>
				</Col>
				<Col md={10} xs={24}>
					<Box title="دارایی های شما در سامانه" gray>
						<Row type="flex" justify="center" style={{ marginBottom: "10px" }}>
							<Col span={10}>موجودی طلا</Col>
							<Col span={10}>موجودی ریالی</Col>
						</Row>
						<Row type="flex" justify="center" style={{ marginBottom: "10px" }}>
							<Col span={10}>{props.user.goldCredit} گرم</Col>
							<Col span={10}>{addCommas(props.user.balance)} ریال</Col>
						</Row>
					</Box>
				</Col>
			</Row>
			<h3>مقدار طلای درخواستی خود را برای فروش به ریال یا به گرم وارد کنید:</h3>
			<Form onSubmit={handleSubmit}>
				<Form.Item>
					<Radio.Group
						name="radiogroup"
						value={selectedRadio}
						onChange={e => {
							const { value } = e.target;
							setSelectedRadio(value);
							setTimeout(() => {
								props.form.validateFields(["unit", "amount"], { force: true });
								if (value === "amount") {
									props.form.resetFields(["unit"]);
								} else {
									props.form.resetFields(["amount"]);
								}
							}, 0);
						}}
					>
						<Form.Item>
							<Radio value="unit">
								<span style={{ display: "inline-block", textAlign: "right", width: 100 }}>
									مقدار طلا
								</span>
							</Radio>
							{getFieldDecorator("unit", {
								rules: [
									{
										validator: handleUnitValidator,
									},
								],
							})(
								<InputNumber
									step={0.004}
									disabled={selectedRadio === "amount"}
									parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
									onChange={(val: any) => {
										const value = Number(val);
										if (value < 0) {
											return;
										}

										const amount = Math.round(value * price);
										setFieldsValue({
											amount,
										});
									}}
								/>,
							)}
							<span style={{ marginRight: 10 }}>گرم</span>
						</Form.Item>
						<Form.Item>
							<Radio value="amount">
								<span style={{ display: "inline-block", textAlign: "right", width: 100 }}>
									مبلغ دریافتی
								</span>
							</Radio>
							{getFieldDecorator("amount", {
								rules: [
									{
										validator: handleAmountValidator,
									},
								],
							})(
								<InputNumber
									disabled={selectedRadio === "unit"}
									formatter={(value: any) =>
										persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
									}
									parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
									onChange={(val: any) => {
										const value = Number(val);
										if (value < 0) {
											return;
										}

										const unit = (value / price).toFixed(4);
										debouncedFunction(value);
										setFieldsValue({
											unit,
										});
									}}
								/>,
							)}

							<span style={{ marginRight: 10 }}>ریال</span>
						</Form.Item>
					</Radio.Group>
				</Form.Item>

				<Maybe condition={state.current >= 1}>
					<Button onClick={() => dispatch({ current: state.current - 1 })} primary>
						بازگشت
					</Button>
				</Maybe>

				<Button disabled={insufficient} htmlType="submit" green>
					ادامه درخواست
					<Icon type="arrow-left" style={{ verticalAlign: "middle", marginRight: "8px" }} />
				</Button>
			</Form>
		</div>
	);
};

export default Form.create({ name: "FirstStep" })(inject("user")(observer(FirstStep)));
